import {
  collection, query, orderBy, limit, getDocs, getDoc, doc,
  addDoc, updateDoc, deleteDoc, serverTimestamp, increment, where, setDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import type { BoardPost, BoardComment } from '../types';

function boardCol(lang: string) { return `board-${lang}`; }

export async function getPosts(lang: string, maxCount = 100): Promise<BoardPost[]> {
  const q = query(collection(db, boardCol(lang)), orderBy('createdAt', 'desc'), limit(maxCount));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BoardPost));
}

export async function getPost(lang: string, postId: string): Promise<BoardPost | null> {
  const snap = await getDoc(doc(db, boardCol(lang), postId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as BoardPost;
}

export async function createPost(lang: string, data: {
  title: string;
  content: string;
  prefix: string;
  authorId: string;
  authorNickname: string;
  isNotice?: boolean;
  isReleaseNote?: boolean;
  isInfoIconLinked?: boolean;
}): Promise<string> {
  const ref = await addDoc(collection(db, boardCol(lang)), {
    ...data,
    isNotice: data.isNotice ?? false,
    isAdminOnly: false,
    isReleaseNote: data.isReleaseNote ?? false,
    isInfoIconLinked: data.isInfoIconLinked ?? false,
    likeCount: 0,
    commentCount: 0,
    views: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updatePost(lang: string, postId: string, data: Partial<BoardPost>): Promise<void> {
  const { id, ...rest } = data as Record<string, unknown>;
  await updateDoc(doc(db, boardCol(lang), postId), { ...rest, updatedAt: serverTimestamp() });
}

export async function deletePost(lang: string, postId: string): Promise<void> {
  await deleteDoc(doc(db, boardCol(lang), postId));
}

export async function getComments(lang: string, postId: string): Promise<BoardComment[]> {
  const q = query(collection(db, boardCol(lang), postId, 'boardComments'), orderBy('createdAt', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BoardComment));
}

export async function deleteComment(lang: string, postId: string, commentId: string): Promise<void> {
  await deleteDoc(doc(db, boardCol(lang), postId, 'boardComments', commentId));
  await updateDoc(doc(db, boardCol(lang), postId), { commentCount: increment(-1) });
}

export async function addComment(lang: string, postId: string, data: {
  content: string;
  authorId: string;
  authorNickname: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, boardCol(lang), postId, 'boardComments'), {
    ...data,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, boardCol(lang), postId), { commentCount: increment(1) });
  return ref.id;
}

export async function getPrefixOptions(lang: string): Promise<{ key: string; label: string }[]> {
  const defaults = [
    { key: 'none', label: lang === 'ko' ? '말머리 없음' : 'None' },
    { key: 'question', label: lang === 'ko' ? '질문' : 'Question' },
    { key: 'frequencyShare', label: lang === 'ko' ? '주파수 공유' : 'Frequency Share' },
    { key: 'review', label: lang === 'ko' ? '후기' : 'Review' },
  ];
  try {
    const snap = await getDoc(doc(db, 'boardSettings', `board-${lang}-prefixes`));
    if (snap.exists() && snap.data().options) return snap.data().options;
    return defaults;
  } catch { return defaults; }
}

export async function savePrefixOptions(lang: string, options: { key: string; label: string }[]): Promise<void> {
  await setDoc(doc(db, 'boardSettings', `board-${lang}-prefixes`), { options });
}
